import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/ui";
import { X, Trophy, MapPin, CalendarDays, Loader2, Medal, DollarSign } from "lucide-react";

function fmtDate(d) {
  if (!d) return "TBA";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return d; }
}

export default function ContestantCard({ open, rodeoId, name, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !rodeoId || !name) return;
    setLoading(true); setData(null);
    api.get(`/contestants/card?rodeo_id=${rodeoId}&name=${encodeURIComponent(name)}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [open, rodeoId, name]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[3000] flex items-end sm:items-center justify-center p-0 sm:p-4"
         data-testid="contestant-card-overlay" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
           data-testid="contestant-card" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#111827] text-white px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-lg font-bold leading-tight" data-testid="contestant-card-name">{name}</div>
            {data?.rodeo_name && <div className="text-xs text-slate-300 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{data.rodeo_name}</div>}
          </div>
          <button onClick={onClose} data-testid="contestant-card-close" className="text-slate-300 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {loading && <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>}

          {!loading && data && !data.error && (
            <>
              {/* Finished results */}
              {data.finished && data.results.length > 0 && (
                <div data-testid="contestant-card-results">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[#C8000F] mb-2 flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> How they placed here</div>
                  <div className="space-y-2">
                    {data.results.map((r, i) => (
                      <div key={i} className="border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-sm text-slate-800">{r.event}</div>
                          <div className="text-xs text-slate-500">
                            {r.place ? <span className="font-medium text-slate-700">{r.place} place</span> : "No place"}
                            {r.value ? <span> · {r.value}</span> : ""}
                            {r.stock ? <span> · on {r.stock}</span> : ""}
                          </div>
                        </div>
                        <div className={`text-sm font-bold ${r.earnings > 0 ? "text-[#3B8A2B]" : "text-slate-400"}`}>{r.earnings > 0 ? money(r.earnings) : "$0"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming draw */}
              {(!data.finished) && data.draw.length > 0 && (
                <div data-testid="contestant-card-draw">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[#F59E0B] mb-2 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Where they're drawn</div>
                  <div className="space-y-2">
                    {data.draw.map((d, i) => (
                      <div key={i} className="border border-slate-200 rounded-lg px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-sm text-slate-800">{d.event}{d.role ? ` · ${d.role}` : ""}</div>
                          <div className="text-xs font-bold text-slate-600">#{d.position}</div>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {d.performance_label || "Perf TBA"} · {fmtDate(d.performance_date)}
                          {d.stock ? <span> · draws {d.stock}</span> : ""}
                          {d.partner ? <span> · with {d.partner}</span> : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* When we have neither */}
              {!data.finished && data.draw.length === 0 && (
                <div className="text-sm text-slate-500 py-2">No draw or result details on file for this rodeo yet.</div>
              )}

              {/* Season snapshot */}
              {data.season && (
                <div className="border-t border-slate-100 pt-3" data-testid="contestant-card-season">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1"><Medal className="w-3.5 h-3.5" /> Season so far</div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2">
                      <div className="text-[10px] text-slate-400 uppercase">Total won</div>
                      <div className="text-lg font-bold text-[#3B8A2B] flex items-center"><DollarSign className="w-4 h-4" />{Number(data.season.total_won).toLocaleString()}</div>
                    </div>
                    {data.season.top_event && (
                      <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2">
                        <div className="text-[10px] text-slate-400 uppercase">Top event</div>
                        <div className="text-sm font-bold text-slate-800">{data.season.top_event}</div>
                        {data.season.rank && <div className="text-xs text-slate-500">#{data.season.rank} in standings</div>}
                      </div>
                    )}
                  </div>
                  {data.season.by_event.length > 1 && (
                    <div className="flex flex-wrap gap-1.5">
                      {data.season.by_event.map((e, i) => (
                        <span key={i} className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{e.event}: {money(e.won)}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!data.season && (
                <div className="text-xs text-slate-400 border-t border-slate-100 pt-3">Not found in this season's standings yet.</div>
              )}
            </>
          )}

          {!loading && data?.error && <div className="text-sm text-red-500 py-4">Couldn't load this contestant. Try again.</div>}
        </div>
      </div>
    </div>
  );
}
